import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike } from '@krp/client-runtime'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import type { CommandView } from '../../../shared/model/commandView'
import { ONEC_PHOTO_URLS } from '../api/urls'
import { photoProductKey } from './keys'
import { isPhotoOutcome, PHOTO_OUTCOME_VIEW } from './outcomes'
import { photoPreviewUrl } from './photoFiles'
import type { PhotoFrameVm, PhotoOperation, PhotosIntentState, PhotoUploadViewVm, ProductPhotosVm } from './types'

export interface ProductPhotosVmInput {
  resourceId: string
  refKey: string
  photos: QueryResultLike<OneCEnvelope<string[]>>
  intent: PhotosIntentState
}

const EMPTY_FRAMES: readonly PhotoFrameVm[] = []
const EMPTY_COMMANDS: ProductPhotosVm['commands'] = []

/** Слова этапа загрузки. Ход «Отправляем -> Отправлено -> Сохраняем -> Готово» читается и без кольца. */
function stageOf(phase: NonNullable<PhotoOperation['phase']>, fraction: number | undefined): string {
  if (phase === 'preparing') return 'Готовим снимок…'
  if (phase === 'saving') return 'Отправлено. Сохраняем в 1С…'
  return fraction === undefined ? 'Отправляем…' : `Отправляем… ${Math.round(fraction * 100)}%`
}

/** Идущая загрузка снимка: превью, кольцо по реальным байтам, слова этапа. */
export function uploadViewOf(operation: PhotoOperation): PhotoUploadViewVm | null {
  if (operation.action !== 'upload' || operation.status !== 'sending') return null
  const phase = operation.phase ?? 'sending'
  const fraction =
    phase === 'sending' && operation.total !== null && operation.total > 0 ? Math.min(1, operation.loaded / operation.total) : undefined
  return {
    key: operation.key,
    fileName: operation.fileName ?? 'снимок',
    previewUrl: photoPreviewUrl(operation.fileToken),
    phase,
    fraction,
    loaded: operation.loaded,
    total: operation.total,
    stage: stageOf(phase, fraction),
    cancelable: phase === 'sending',
  }
}

function commandOf(operation: PhotoOperation): CommandView & { key: string } {
  const label = operation.action === 'main' ? 'Главное фото' : `Загрузка ${operation.fileName ?? 'снимка'}`
  const base = { key: operation.key, label, error: operation.error, resendable: operation.resendable }
  if (operation.outcome !== null) {
    const known = isPhotoOutcome(operation.outcome) ? PHOTO_OUTCOME_VIEW[operation.outcome] : { text: `Итог: ${operation.outcome}`, done: true }
    return { ...base, status: known.done ? 'succeeded' : 'failed', message: known.text, resendable: false }
  }
  return { ...base, status: operation.status, message: operation.error?.message ?? null }
}

export const makeSelectProductPhotosVm = () => {
  const selectView = createSelector(
    [
      (input: ProductPhotosVmInput) => input.photos.isUninitialized,
      (input: ProductPhotosVmInput) => input.photos.isLoading,
      (input: ProductPhotosVmInput) => input.photos.isError,
      (input: ProductPhotosVmInput) => input.photos.isSuccess,
      (input: ProductPhotosVmInput) => input.photos.error,
      (input: ProductPhotosVmInput) => input.photos.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data) =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        (value) => value.data.length === 0,
      ),
  )
  const selectOperations = createSelector(
    [
      (input: ProductPhotosVmInput) => input.intent.operations,
      (input: ProductPhotosVmInput) => photoProductKey(input.resourceId, input.refKey),
    ],
    (operations, prefix) =>
      Object.values(operations)
        .filter((operation) => operation.key.startsWith(`${prefix}|`))
        .sort((left, right) => left.startedAt.localeCompare(right.startedAt)),
  )
  const selectFrames = createSelector(
    [
      selectView,
      (input: ProductPhotosVmInput) => input.photos.data,
      (input: ProductPhotosVmInput) => input.resourceId,
      (input: ProductPhotosVmInput) => input.refKey,
      (input: ProductPhotosVmInput) => input.intent.selected[photoProductKey(input.resourceId, input.refKey)],
    ],
    (view, data, resourceId, refKey, selected): readonly PhotoFrameVm[] => {
      if (view.status !== 'ready' || data === undefined || data.data.length === 0) return EMPTY_FRAMES
      const chosen = selected !== undefined && data.data.includes(selected) ? selected : data.data[0]
      return data.data.map((fileKey, index) => ({
        fileKey,
        url: ONEC_PHOTO_URLS.file(resourceId, refKey, fileKey),
        // Служба отдаёт главный первым.
        isMain: index === 0,
        selected: fileKey === chosen,
      }))
    },
  )
  return createSelector(
    [
      (input: ProductPhotosVmInput) => input.resourceId,
      (input: ProductPhotosVmInput) => input.refKey,
      selectView,
      selectFrames,
      selectOperations,
      (input: ProductPhotosVmInput) => input.photos.data?.configurationVersion ?? null,
    ],
    (resourceId, refKey, query, frames, operations, configurationVersion): ProductPhotosVm => {
      const busy = (action: PhotoOperation['action']) =>
        operations.some((operation) => operation.action === action && (operation.status === 'sending' || operation.status === 'unknown'))
      return {
        resourceId,
        refKey,
        query,
        frames,
        current: frames.find((frame) => frame.selected) ?? null,
        // Идущая загрузка рисуется карточкой с превью и кольцом (uploadView), а не строкой статуса.
        commands: operations.length === 0 ? EMPTY_COMMANDS : operations.filter((operation) => uploadViewOf(operation) === null).map(commandOf),
        uploadView: operations.map(uploadViewOf).find((view) => view !== null) ?? null,
        configurationVersion,
        upload: { busy: busy('upload') },
        mainBusy: busy('main'),
      }
    },
  )
}
